import React from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useNavigate, useParams } from "react-router";
import { LongTextLabel, Toast } from "@sscs/ui";
import { Dialog } from "Components";
import { useProjectsRoute, useReports } from "Hooks";
import { useGetPackageQuery } from "Services";
import { Package } from "Types";
import { apiErrorToast } from "Utils";
import { useDeletePackageMutation } from "../../services/projects.api";
import styles from "./deletePackageDialog.module.scss";

type DeletePackageDialogProps = {
  packageDataOrId: Package | string;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
};

const DeletePackageDialog = ({
  packageDataOrId,
  projectName,
  isOpen,
  onClose,
}: DeletePackageDialogProps): React.JSX.Element | null => {
  const { groupId } = useParams();
  const { closeAllReports } = useReports();
  const { data } = useGetPackageQuery(
    typeof packageDataOrId === "string"
      ? { packageId: packageDataOrId }
      : skipToken,
  );
  const packageInfo =
    data ?? (typeof packageDataOrId === "object" ? packageDataOrId : null);
  const projectsRoute = useProjectsRoute(packageInfo?.project_id);
  const navigate = useNavigate();
  const { packageId } = useParams();
  const [deletePackage] = useDeletePackageMutation();

  if (!packageInfo) return null;

  const handleDelete = async () => {
    try {
      await deletePackage({
        packageId: packageInfo.package_id,
      }).unwrap();

      Toast.show({
        title: `Package deleted`,
        type: "success",
      });

      // Close all opened reports for this package
      closeAllReports({ groupId, projectName, packageName: packageInfo.name });
      // return to the project if we've deleted the package we have selected in the project tree
      if (packageId === packageInfo.package_id) navigate(projectsRoute);
    } catch (error) {
      apiErrorToast(error, "Could not delete package");
    }

    onClose();
  };

  return (
    <Dialog
      title={`Delete package ${packageInfo.name}?`}
      confirmText="Yes, delete this package"
      isOpen={isOpen}
      onCancel={onClose}
      onClose={onClose}
      onConfirm={handleDelete}
      cancelButtonColor="primary"
      confirmButtonColor="error"
      dangerZone={packageInfo.name}
    >
      <div>
        <div className={styles.content}>
          Do you want to delete package
          <LongTextLabel
            label={packageInfo.name}
            className={styles["delete-package"]}
          />
          ?
        </div>
        If the package is deleted{" "}
        <b>
          {packageInfo.version_count.toLocaleString()} uploaded version
          {packageInfo.version_count !== 1 ? "s" : ""}
        </b>{" "}
        and their SDLC reports will be deleted.
      </div>
    </Dialog>
  );
};

export default DeletePackageDialog;
